/**
 * Map Layers Utility
 * Utilities for managing isochrone layers on the map.
 */

import type { Map as MapboxMap, GeoJSONSource } from 'mapbox-gl';

/**
 * Layer IDs for isochrone layers
 */
const ISOCHRONE_SOURCE_ID = 'isochrone-source';
const ISOCHRONE_LAYER_ID = 'isochrone-layer';

/**
 * Clears all isochrone layers from the map.
 * @param map - Mapbox map instance
 */
export function clearIsochroneLayers(map: MapboxMap): void {
  // Ensure map is loaded
  if (!map || !map.loaded()) {
    return;
  }

  try {
    // Remove outline layer if it exists
    const outlineLayerId = `${ISOCHRONE_LAYER_ID}-outline`;
    if (map.getLayer(outlineLayerId)) {
      map.removeLayer(outlineLayerId);
    }

    // Remove fill layer if it exists
    if (map.getLayer(ISOCHRONE_LAYER_ID)) {
      map.removeLayer(ISOCHRONE_LAYER_ID);
    }

    // Remove source if it exists
    if (map.getSource(ISOCHRONE_SOURCE_ID)) {
      map.removeSource(ISOCHRONE_SOURCE_ID);
    }
  } catch (error) {
    // Silently handle errors if layers don't exist
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.warn('Error clearing isochrone layers:', error);
    }
  }
}

/**
 * Renders one or more isochrone layers on the map.
 * Optimized to only update source data instead of clearing/recreating layers to prevent blinking.
 * Can render multiple isochrones stacked to show progression (e.g., at 2x speed showing 1-min and 2-min).
 * @param map - Mapbox map instance
 * @param polygons - Single polygon or array of polygons with their times
 * @param time - Travel time in minutes (for styling, used when single polygon)
 */
export function renderIsochroneLayer(
  map: MapboxMap,
  polygon: GeoJSON.Polygon | Array<{ polygon: GeoJSON.Polygon; time: number }>,
  time?: number
): void {
  // Ensure map is loaded
  if (!map || !map.loaded()) {
    return;
  }

  // Normalize input: convert single polygon to array format
  const features: Array<{ polygon: GeoJSON.Polygon; time: number }> = Array.isArray(polygon)
    ? polygon
    : [{ polygon: polygon as GeoJSON.Polygon, time: time || 0 }];

  const sourceData = {
    type: 'FeatureCollection' as const,
    features: features.map(({ polygon: p, time: t }) => ({
      type: 'Feature' as const,
      geometry: p,
      properties: { time: t },
    })),
  };

  // Try to update existing source (fast, no blinking)
  const source = map.getSource(ISOCHRONE_SOURCE_ID);
  if (source && source.type === 'geojson') {
    try {
      (source as GeoJSONSource).setData(sourceData);
      return; // Successfully updated, exit early
    } catch (error) {
      // If update fails, fall back to recreating
      if (import.meta.env.VITE_DEBUG === 'true') {
        console.warn('Failed to update isochrone source, recreating:', error);
      }
    }
  }

  // Source doesn't exist or update failed - need to create it
  try {
    // Clear existing layers first only if we need to recreate
    clearIsochroneLayers(map);

    // Add new source
    map.addSource(ISOCHRONE_SOURCE_ID, {
      type: 'geojson',
      data: sourceData,
    });

    // Add fill layer
    map.addLayer({
      id: ISOCHRONE_LAYER_ID,
      type: 'fill',
      source: ISOCHRONE_SOURCE_ID,
      paint: {
        'fill-color': '#4A90E2',
        'fill-opacity': 0.3,
      },
    });

    // Add outline layer
    map.addLayer({
      id: `${ISOCHRONE_LAYER_ID}-outline`,
      type: 'line',
      source: ISOCHRONE_SOURCE_ID,
      paint: {
        'line-color': '#4A90E2',
        'line-width': 2,
        'line-opacity': 0.6,
      },
    });
  } catch (error) {
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.warn('Error rendering isochrone layer:', error);
    }
  }
}

